import Bowser from 'bowser';
import Analytics, { customTrack } from '@cashfree-intl/analytics';
import { CURRENT_NODE_ENV, POSTHOG_API_KEY, POSTHOG_API_HOST } from './env';

import {
  getDeviceId,
  getDeviceIdSource,
  getRequestUrlAndToken,
} from './common';
import { touchSentryAnalyticsEvent } from './sentry/analyticsEvent';
import { PROD, TRACK_EVENT } from './constants';
// import request from './request';
import Emitter from './emitter';
import EVENTS from '../constants/analytics';
import { getActionType } from './getActionType';

const DEFAULT_ACTION_TYPE = 'click';

const init = async () => {
  // below methods set deviceid & deviceIdSource to localstorage if not already
  await getDeviceId();
  getDeviceIdSource();
};

const getFinalEventProperties = (eventProperties, eventName) => {
  const eProps = eventProperties ? { ...eventProperties } : {};
  if (!eProps.actionType) {
    eProps.actionType = eventName
      ? getActionType(eventName)
      : DEFAULT_ACTION_TYPE;
  }
  return eProps;
};

const sendEvent = (etype, eventProperties, appType = 'PGDB', email) => {
  if (!etype) return;

  touchSentryAnalyticsEvent(etype, appType);

  const eProps = getFinalEventProperties(eventProperties);
  const eventType = `${appType}: ${etype.toLowerCase()}`;

  if (window.Moengage) {
    if (email) {
      window.Moengage.add_email(email);
    }
    window.Moengage.track_event(eventType, eProps);
  }
};

const track = async (etype, eventProperties, apptype = 'PGDB') => {
  if (!etype) return;

  touchSentryAnalyticsEvent(etype, apptype);

  const eProps = getFinalEventProperties(eventProperties);

  const visitorId = await getDeviceId();
  const browserData = Bowser.parse(window.navigator.userAgent);

  const browserName = browserData.browser.name
    ? browserData.browser.name
    : 'NoBrowser';

  let osName = browserData.os.name ? browserData.os.name : 'NoOS';
  osName = `${browserName}-${osName}`;

  let browserVersion = browserData.browser.version
    ? browserData.browser.version
    : '0.0.1';

  // eslint-disable-next-line prefer-destructuring
  browserVersion = browserVersion.split('.')[0];
  const tracker = {
    user_id: visitorId,
    device_id: visitorId,
    os_name: osName,
    os_version: browserVersion,
    platform: browserData.platform.type
      ? browserData.platform.type
      : 'NoPlatform',
    device_brand: browserData.platform.vendor
      ? browserData.platform.vendor
      : 'NA',
    device_model: browserData.platform.model
      ? browserData.platform.model
      : 'NA',
    language: navigator.language || navigator.userLanguage,
    event_type: `${apptype}: ${etype.toLowerCase()}`,
  };

  if (eProps) {
    tracker.event_properties = eProps;
  }

  // const base64Data = btoa(JSON.stringify(tracker));

  // const url = '/common/events/track';
  // const options = {
  //   method: 'POST',
  //   body: JSON.stringify({
  //     data: base64Data,
  //   }),
  // };

  // try {
  //   await request(url, options);
  // } catch (err) {
  //   // eslint-disable-next-line no-console
  //   console.log(err);
  // }
};

const trackEvent = (etype, eprops, apptype) => () => {
  const eProps = getFinalEventProperties(eprops);

  const { token } = getRequestUrlAndToken();
  if (token) track(etype, eProps, apptype);
  else sendEvent(etype, eProps, apptype);
};

const log = (logs) => {
  try {
    const base64Data = btoa(JSON.stringify(logs));
    // eslint-disable-next-line no-console
    console.log(base64Data);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log('Logging disabled', err);
  }
};

const trackEventWithNewNomenclature = ({ eventName, eventProperties = {} }) => {
  touchSentryAnalyticsEvent(eventName, 'PG');

  const eProps = getFinalEventProperties(eventProperties, eventName);

  const envObj = process.env;
  const isProdEnv = envObj.NODE_ENV === PROD;
  Analytics.track({
    isDebug: !isProdEnv,
    eventName,
    eventProperties: eProps,
  });
};

const eventHandler = (args) => {
  const eProps = getFinalEventProperties(args);
  const { token } = getRequestUrlAndToken();
  if (token) track(EVENTS.API_RESPONSE_TIME, eProps);
  else sendEvent(EVENTS.API_RESPONSE_TIME, eProps, 'OB');
};

Emitter.on(TRACK_EVENT, eventHandler);

export const initAnalytics = () => {
  Analytics.init({
    product: 'CDB',
    token: null,
    isDebug: true,
    enableNonTokenFlow: true,
    options: {
      posthog: {
        key: POSTHOG_API_KEY,
        host: POSTHOG_API_HOST,
      },
    },
  });
};

const trackWithSDK = (eventName, eventProperties, appType = 'CDB') => {
  if (!eventName) {
    return;
  }

  const eProps = getFinalEventProperties(eventProperties);

  /**
   * Adding ":>" in eventName, because when Analytics.track() is used for tracking,
   * it adds ":>" in the event name, hence making sure, the same event gets used in both the flows
   */
  const eventType = `${appType}:> ${eventName.toLowerCase()}`;

  touchSentryAnalyticsEvent(eventType);

  /**
   * this internally calls posthog.track()
   */
  customTrack(eventType, {
    ...(eProps || {}),
    NODE_ENV: CURRENT_NODE_ENV,
  });
};

export default {
  init,
  track,
  trackEvent,
  sendEvent,
  log,
  trackEventWithNewNomenclature,
  trackWithSDK,
};